import React, { useState } from 'react';
import { Tabs, Card } from 'antd';
import {
  BarChartOutlined,
  GiftOutlined,
  ClockCircleOutlined,
  ShoppingOutlined,
  RetweetOutlined,
  DollarOutlined,
  LockOutlined,
  CrownOutlined
} from '@ant-design/icons';
import MemberAnalytics from './MemberAnalytics';
import CouponAnalytics from './CouponAnalytics';
import TimeAnalytics from './TimeAnalytics';
import ProductAnalytics from './ProductAnalytics';
import RepeatAnalytics from './RepeatAnalytics';
import LTVAnalytics from './LTVAnalytics';

const { TabPane } = Tabs;

const isUnlocked = true;

const CouponAnalyticsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('member');

  if (!isUnlocked) {
    return (
      <div className="p-6">
        {/* Suspension Notice */}
        <div style={{
          border: '2px solid #dc2626',
          borderRadius: '8px',
          padding: '16px 20px',
          marginBottom: '16px',
          backgroundColor: '#fef2f2'
        }}>
          <h3 style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '15px', marginBottom: '8px' }}>
            機能の一時停止について
          </h3>
          <p style={{ color: '#991b1b', fontSize: '13px', margin: 0, lineHeight: 1.7 }}>
            本機能は一度デモ動作いたしましたが、確認したところ一部タブにおいて不具合が見つかりました。
            当初の実装予定範囲外であったため、エラー修正および本番運用に耐えうる品質での再構築には
            <strong>プレミアムオプション契約</strong>が必要となります。
            ご興味のある方は開発担当までお問い合わせください。
          </p>
        </div>

        {/* Premium Overlay */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px',
          padding: '48px 32px',
          color: 'white',
          textAlign: 'center',
          marginBottom: '24px',
          boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)'
        }}>
          <CrownOutlined style={{ fontSize: '64px', marginBottom: '16px', color: '#ffd700' }} />
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '8px', color: 'white' }}>
            クーポン履歴分析
          </h1>
          <p style={{ fontSize: '16px', opacity: 0.9, marginBottom: '32px' }}>
            クーポンの発行・使用状況を多角的に分析できるプレミアム機能です
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {[
              { icon: <BarChartOutlined />, title: '会員別分析', desc: '会員ごとのクーポン利用状況' },
              { icon: <GiftOutlined />, title: 'クーポン別分析', desc: 'クーポン種別ごとの効果' },
              { icon: <ClockCircleOutlined />, title: '曜日・時間帯別', desc: '利用ピーク時間の把握' },
              { icon: <ShoppingOutlined />, title: '併用購入商品', desc: 'クーポンと一緒に購入される商品' },
              { icon: <RetweetOutlined />, title: 'リピート率分析', desc: '再利用率の推移' },
              { icon: <DollarOutlined />, title: 'LTV分析', desc: '顧客生涯価値の分析' },
            ].map((item, idx) => (
              <div key={idx} style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '12px',
                padding: '16px',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>{item.icon}</div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{item.title}</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Blurred Preview */}
        <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '12px' }}>
          <div style={{
            filter: 'blur(8px)',
            pointerEvents: 'none',
            opacity: 0.5
          }}>
            <Card className="shadow-lg">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  🎫 クーポン履歴管理システム
                </h1>
                <p className="text-gray-600">
                  クーポンの発行・使用状況を多角的に分析できます
                </p>
              </div>
              <div style={{ height: '300px', background: '#f5f5f5', borderRadius: '8px' }} />
            </Card>
          </div>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center'
          }}>
            <LockOutlined style={{ fontSize: '48px', color: '#667eea' }} />
            <p style={{ color: '#667eea', fontWeight: 'bold', marginTop: '8px' }}>プレミアム機能</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card className="shadow-lg">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            🎫 クーポン履歴管理システム
          </h1>
          <p className="text-gray-600">
            クーポンの発行・使用状況を多角的に分析できます
          </p>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
          type="card"
        >
          <TabPane
            tab={
              <span>
                <BarChartOutlined />
                会員別分析
              </span>
            }
            key="member"
          >
            <MemberAnalytics />
          </TabPane>

          <TabPane
            tab={
              <span>
                <GiftOutlined />
                クーポン別分析
              </span>
            }
            key="coupon"
          >
            <CouponAnalytics />
          </TabPane>

          <TabPane
            tab={
              <span>
                <ClockCircleOutlined />
                曜日・時間帯別
              </span>
            }
            key="time"
          >
            <TimeAnalytics />
          </TabPane>

          <TabPane
            tab={
              <span>
                <ShoppingOutlined />
                併用購入商品
              </span>
            }
            key="product"
          >
            <ProductAnalytics />
          </TabPane>

          <TabPane
            tab={
              <span>
                <RetweetOutlined />
                リピート率分析
              </span>
            }
            key="repeat"
          >
            <RepeatAnalytics />
          </TabPane>

          <TabPane
            tab={
              <span>
                <DollarOutlined />
                LTV分析
              </span>
            }
            key="ltv"
          >
            <LTVAnalytics />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default CouponAnalyticsDashboard;
